import type { RumEvent } from '@datadog/browser-rum';
import { nextjsPlugin } from '@datadog/browser-rum-nextjs';
// Deep import: `startNextjsView` is the plugin's own view-start path (it consumes the
// URL captured by `onRouterTransitionStart`) but is not re-exported from the package
// entry. Same rationale as the deep import in `app/components/DatadogRouteTracker.tsx`
// — a moved internal path fails loudly at build time. Resolves to the same module
// instance as the entry above, so it shares the plugin's captured state.
import { startNextjsView } from '@datadog/browser-rum-nextjs/esm/domain/nextjsPlugin';

import { markLoaded as markDatadogSessionLoaded } from 'lib/datadogSession';
import { useAuthStore } from 'lib/stores/authStore';
import * as datadogRum from 'lib/telemetry/datadogRum';
import { installFetchFailureContext } from 'lib/telemetry/fetchFailureContext';
import { getInitialRumViewName } from 'lib/telemetry/initialRumView';
import { startMainThreadPublishing, stopMainThreadBlocking } from 'lib/telemetry/mainThreadBlocking';
import { datadogContextManager } from 'transport/datadogContextManager';
import { memoryMonitor } from 'transport/memoryMonitor';
import config from 'utils/config';

/**
 * Datadog RUM service module — a sink, not reactive state: init/dispose
 * orchestration and the RUM noise filter, with no observable state read by
 * any component. Session identify/clear already runs through
 * `lib/datadogSession.ts`, wired into `lib/stores/sessionEffects.ts`.
 */

const IGNORABLE_ERROR_TYPES = new Set([
    'AbortError',
    'TimeoutError', // AbortSignal.timeout() — expected on slow prefetches
    'IgnoredEventCancel', // react-dom passive touchend intervention
]);

const IGNORABLE_MESSAGE_SUBSTRINGS = [
    /**
     * Network errors
     */
    'AbortError',
    'NS_BINDING_ABORTED',
    'Failed to fetch RSC payload',
    'NetworkError when attempting to fetch resource',
    'Load failed', // Mobile Safari service worker NetworkError
    'network error', // Chrome: TypeError on fetch network failure

    /**
     * Intentional request cancellations
     */
    'Cancel request due to page not being visible.', // transport layer cancels in-flight requests when tab is hidden

    /**
     * Browser/extension errors
     */
    'SCDynimacBridge', // snapchat browser?
    "Failed to execute 'removeChild' on 'Node'", // DOM manipulation by extensions
    'Looks like there is an error in the background page', // Fixinator extension
    'Unhandled error response received for message', // extension runtime.sendMessage
    'Invalid call to runtime.sendMessage', // Chrome Extension API
    'trustwallet', // Trust Wallet extension failing to find its own injected global

    /**
     * Bot traffic
     */
    'Object Not Found Matching Id', // CefSharp crawlers (Outlook/O365 SafeLinks) — bot JS bridge, never a real user

    /**
     * Ad blocker/network blocking
     */
    'ERR_BLOCKED_BY_CLIENT',

    /**
     * Browser quirks
     */
    'ResizeObserver loop', // harmless browser throttling
    'Script error', // cross-origin errors with no info
    'can\'t access property "postMessage"', // Firefox: comlink windowEndpoint called with null window (detached iframe)
    'The operation failed for an unknown transient reason', // IndexedDB OOM
    'Database deleted by request of the user', // user cleared browser data

    /**
     * Third-party
     */
    'invalid origin', // postMessage origin mismatch
    'selectPlacements', // AppLovin ad SDK race on /sign-up/survey, GTM-injected anonymous frame
    // Statsig session-replay (rrweb) recorder failing to detach listeners from a
    // cross-origin iframe Window — see also handleStatsigReplayError in
    // statsigReplay.ts, which suppresses the same error inside rrweb's errorHandler.
    "Failed to read a named property 'removeEventListener' from 'Window'",
];

const THIRD_PARTY_STACK_PATTERNS = [
    'lightweight-charts.esm.production.js', // TradingView
    '@bprogress/react/dist', // invalid URL construction (e.g. `email:`)
    'DocumentProjectionNode.mjs', // Framer Motion
    'EngagementManager.js', // engagement SDK
    'canvas_advanced/canvas_advanced.mjs', // Rive WASM
    'rive.ts', // Rive companion
    'tools/instrumentMethod', // Datadog SDK internal fetch failures
    'device-risk-sdk', // Socure SDK mediaDevices unhandled rejection
    'gtm.js', // GTM custom tag errors — misconfigured tags fire on every nuqs URL update
];

// Matches versioned API endpoints only. Static assets, _next chunks, and page
// HTML use different URL shapes and must NOT be suppressed — a missing chunk
// on a first-party origin signals a broken deploy and should still alert.
const FIRST_PARTY_API_URL_PATTERN = /^https?:\/\/api\.elections\.kalshi\.com\/v1\//;
const THIRD_PARTY_RESOURCE_URL_PATTERN = /^https?:\/\/(?![^\s/]*kalshi)[^\s/]+/;
const NO_ERROR_STACK_MESSAGE = 'No stack, consider using an instance of Error';
// The SDK substitutes 'Empty reason' when a promise rejects with a falsy reason
// (bare reject(), reject(undefined), an error callback invoked with no argument).
const EMPTY_REASON_REJECTION_MESSAGE = 'Uncaught "Empty reason"';
const XHR_ERROR_EVENT_TARGET_PATTERN = /"target"\s*:\s*"\[XMLHttpRequest\]"/;
const SCRIPT_ERROR_EVENT_TARGET_PATTERN = /"target"\s*:\s*"\[HTMLScriptElement\]"/;
const BOT_USER_AGENT_PATTERN = /(?:bot|crawler|spider|crawling)/i;

type RumErrorFields = {
    message?: string;
    source?: string;
    stack?: string;
    handling_stack?: string;
    resource?: {
        url?: string;
        status_code?: number;
    };
};

const isFirstPartyApiUrl = (url: string | undefined): boolean => !!url && FIRST_PARTY_API_URL_PATTERN.test(url);

const hasUsableStack = (stack: string | undefined): boolean => !!stack && stack !== NO_ERROR_STACK_MESSAGE;

const isThirdPartyScriptResourceError = (event: RumEvent, nativeScriptUrl?: string): boolean => {
    const error = event.error as RumErrorFields | undefined;
    const message = error?.message ?? '';
    const resourceUrl = error?.resource?.url ?? nativeScriptUrl;

    return (
        error?.source === 'source' &&
        SCRIPT_ERROR_EVENT_TARGET_PATTERN.test(message) &&
        THIRD_PARTY_RESOURCE_URL_PATTERN.test(resourceUrl ?? '')
    );
};

const getNativeScriptUrl = (domainContext: unknown): string | undefined => {
    if (typeof domainContext !== 'object' || domainContext === null || !('error' in domainContext)) return undefined;

    const originalError = domainContext.error;
    if (typeof originalError !== 'object' || originalError === null || !('target' in originalError)) return undefined;

    const target = originalError.target;
    if (typeof HTMLScriptElement === 'undefined' || !(target instanceof HTMLScriptElement)) return undefined;
    return target.src || undefined;
};

const isOpaqueXhrErrorEvent = (event: RumEvent): boolean => {
    const error = event.error as RumErrorFields | undefined;
    const message = error?.message ?? '';
    const stack = error?.stack;
    const handlingStack = error?.handling_stack;
    const resource = error?.resource;

    return (
        error?.source === 'source' &&
        XHR_ERROR_EVENT_TARGET_PATTERN.test(message) &&
        !hasUsableStack(stack) &&
        !handlingStack &&
        !resource?.url &&
        !resource?.status_code
    );
};

const isOpaqueScriptErrorEvent = (event: RumEvent): boolean => {
    const error = event.error as RumErrorFields | undefined;
    const message = error?.message ?? '';

    return (
        error?.source === 'source' &&
        SCRIPT_ERROR_EVENT_TARGET_PATTERN.test(message) &&
        !hasUsableStack(error?.stack) &&
        !error?.handling_stack &&
        !error?.resource?.url &&
        !error?.resource?.status_code
    );
};

const isEmptyReasonRejectionEvent = (event: RumEvent): boolean => {
    const error = event.error as RumErrorFields | undefined;

    return (
        error?.source === 'source' &&
        error?.message === EMPTY_REASON_REJECTION_MESSAGE &&
        !hasUsableStack(error?.stack) &&
        !error?.handling_stack &&
        !error?.resource?.url &&
        !error?.resource?.status_code
    );
};

const isNonActionableFetchFailure = (event: RumEvent): boolean => {
    const error = event.error as RumErrorFields | undefined;
    if (error?.message !== 'Failed to fetch') return false;

    const context = event.context as { failed_fetch_connectivity?: unknown; failed_fetch_url?: unknown } | undefined;
    if (context?.failed_fetch_connectivity === 'not_connected') return true;

    return (
        typeof context?.failed_fetch_url === 'string' && THIRD_PARTY_RESOURCE_URL_PATTERN.test(context.failed_fetch_url)
    );
};

/**
 * Three-tier filter for RUM errors (addError() calls bypass this — see `source === 'custom'` check):
 *
 * 1. Unactionable noise — error types and message substrings that are never useful regardless of source
 * 2. Bundled third-party — specific stack patterns for vendor code bundled into our domain
 *    (URL regex can't distinguish these since they share kalshi.com URLs)
 * 3. Extensions & external scripts — URL regex on stack traces and error messages catches chrome-extension://,
 *    moz-extension://, and non-kalshi HTTPS origins (message check additionally allows react.dev links)
 */
export const isFirstPartyError = (
    event: RumEvent,
    domainContext?: unknown,
    userAgent = typeof navigator === 'undefined' ? '' : navigator.userAgent,
): boolean => {
    if (event.type !== 'error') return true;

    const nativeScriptUrl = getNativeScriptUrl(domainContext);
    if (nativeScriptUrl && SCRIPT_ERROR_EVENT_TARGET_PATTERN.test(event.error?.message ?? '')) {
        event.context = { ...event.context, native_script_url: nativeScriptUrl };
    }

    // Errors reported via addError() (error boundaries, manual reports) are pre-vetted
    // by the calling code — bypass noise filters so UI-crashing errors always reach Datadog.
    if (event.error?.source === 'custom') return true;

    // CSP violations are almost entirely from browser extensions injecting
    // scripts/styles — not actionable. Use report-to directive for real CSP monitoring.
    if (event.error?.source === 'report' && event.error?.csp) {
        return false;
    }

    // 404s from our own API are expected (deleted/expired resources, optimistic
    // lookups) and not actionable as front-end errors — the backend owns the
    // resource lifecycle. Drop them so they don't page anyone. Third-party 404s
    // are already filtered by the URL tiers below, so this only scopes to first-party.
    if (event.error?.resource?.status_code === 404 && isFirstPartyApiUrl(event.error.resource.url)) {
        return false;
    }

    // Drop script load failures only when Datadog identified a non-Kalshi
    // resource URL. URL-less HTMLScriptElement ErrorEvents can also be
    // first-party chunk failures, so those must keep flowing to RUM.
    if (isThirdPartyScriptResourceError(event, nativeScriptUrl)) {
        return false;
    }

    if (isOpaqueScriptErrorEvent(event) && BOT_USER_AGENT_PATTERN.test(userAgent)) {
        return false;
    }

    // Native XMLHttpRequest ErrorEvents are opaque when the browser gives us no
    // stack or resource URL/status. They dominated RUM without pointing to a
    // failing endpoint; instrumented resource/API errors still pass through.
    if (isOpaqueXhrErrorEvent(event)) {
        return false;
    }

    // Unhandled rejections with a falsy reason carry zero attribution (no stack,
    // no handling_stack, no resource) and dominated RUM noise. First-party
    // rejection sources are normalized to Error instances, so anything still
    // matching the SDK's 'Empty reason' placeholder is third-party/extension
    // code. Rejections that do carry a usable stack or handling_stack still
    // flow through.
    if (isEmptyReasonRejectionEvent(event)) {
        return false;
    }

    if (isNonActionableFetchFailure(event)) {
        return false;
    }

    const message = event.error?.message ?? '';
    const errorType = event.error?.type ?? '';
    const stack = event.error?.stack ?? '';
    const handlingStack = event.error?.handling_stack ?? '';

    // Tier 1: unactionable noise
    const isIgnorableError =
        IGNORABLE_ERROR_TYPES.has(errorType) ||
        IGNORABLE_MESSAGE_SUBSTRINGS.some((substring) => message.includes(substring));

    if (isIgnorableError) {
        return false;
    }

    // Stale deployment: worker tried to importScripts a chunk from a previous deployment
    if (message.includes("importScripts' on 'WorkerGlobalScope'") && message.includes('_next/static/chunks')) {
        return false;
    }

    if (message.includes('Maximum call stack size exceeded') && !stack && !handlingStack) {
        return false; // Chrome Mobile iOS: no frames to act on
    }

    // Tier 2: bundled third-party
    const allStacks = stack + handlingStack;
    if (THIRD_PARTY_STACK_PATTERNS.some((pattern) => allStacks.includes(pattern))) {
        return false;
    }

    // Blob URLs inherit the page origin, so blob:https://kalshi.com/... appears
    // first-party but is actually a third-party SDK worker (e.g. Datadog deflate).
    // Our own workers use module URLs (new URL('./worker.ts', import.meta.url)),
    // not blob URLs, so this only filters third-party worker errors.
    if (/blob:https?:\/\/[^\s/]*kalshi/.test(allStacks)) {
        return false;
    }

    // Extension-injected scripts produce frames with no source URL
    // (e.g. "at renderUI @ :1:273"). Inline <script> errors use
    // <anonymous> in Chrome; our bundled code always has https:// URLs.
    if (allStacks && /\bat\s/.test(allStacks) && !/https?:\/\//.test(allStacks) && !/<anonymous>/.test(allStacks)) {
        return false;
    }

    // Extension-injected inline handlers (onclick="...") produce frames like
    // "at HTMLButtonElement.onclick". React uses addEventListener, never inline
    // handlers, so this is always external code.
    if (/at HTML\w+Element\.on\w+/.test(allStacks)) {
        return false;
    }

    // Tier 3: extensions & external scripts

    /**
     * No URL → regex doesn't match → passes through
     * https://kalshi.com/... → negative lookahead finds "kalshi" in hostname → regex doesn't match → passes through
     * https://www.kalshi.com/... → negative lookahead finds "kalshi" in hostname → regex doesn't match → passes through
     * https://react.dev/... → negative lookahead finds "react.dev" in hostname → regex doesn't match → passes through
     * https://prodregistryv2.org/... → regex matches → filtered
     */
    const thirdPartyMessagePattern = /(https?):\/\/(?![^\s/]*(kalshi|react\.dev))[^\s\/]+/;

    /**
     * No URL → regex doesn't match → passes through
     * https://kalshi.com/... → negative lookahead finds "kalshi" in hostname → regex doesn't match → passes through
     * https://www.kalshi.com/... → negative lookahead finds "kalshi" in hostname → regex doesn't match → passes through
     * https://someother.com/... → regex matches → filtered
     * chrome-extension://... → regex matches (not kalshi) → filtered
     */
    const thirdPartyStackPattern = /(https?|chrome-extension|moz-extension):\/\/(?![^\s/]*kalshi)[^\s\/]+/;

    if (
        thirdPartyStackPattern.test(stack) ||
        thirdPartyStackPattern.test(handlingStack) ||
        thirdPartyMessagePattern.test(message)
    ) {
        return false;
    }

    return true;
};

// Records the browser load-event timestamp for the initial hard navigation.
// PerformanceNavigationTiming entries only exist for full page loads, not
// client-side route changes, so this runs once then disconnects.
// Datadog's own activity tracking remains the source of truth for view loading time.
function setupViewLoadingTime(): void {
    if (typeof PerformanceObserver === 'undefined') return;

    const observer = new PerformanceObserver((list) => {
        for (const entry of list.getEntries() as PerformanceNavigationTiming[]) {
            if (entry.loadEventEnd > 0) {
                datadogRum.addTiming('load_event_end', entry.loadEventEnd);
                observer.disconnect();
                return;
            }
        }
    });
    observer.observe({ type: 'navigation', buffered: true });
}

let started = false;
let loaded = false;
let initialViewStarted = false;
const pendingReplayCalls: Array<() => void> = [];

export const INTERNAL_BACKGROUND_ACTIVITY_URLS = [
    /metrics\/client_event(?:[/?]|$)/,
    /forecast_history/,
    /social\/trades(?:[/?]|$)/,
    /portfolio\/current_value/,
    /portfolio\/balance/,
    /event_positions(?:[/?]|$)/,
    /margin\/balance(?:[/?]|$)/,
    /margin\/isolated\/position_details(?:[/?]|$)/,
    /trade-api\/v2\/margin\/(?:positions|risk)(?:[/?]|$)/,
    /notifications/,
    /exchange\/status/,
    /crypto\/\w+_history/,
    /crypto\/\w+_current/,
    /live_data\/count/,
    /order_books/,
    /_vercel\/insights\/view(?:[/?]|$)/,
];

export const NON_CRITICAL_DISPLAY_ACTIVITY_URLS = [/milestones\/[^/]+\/volume(?:[/?]|$)/, /live_data\/batch(?:[/?]|$)/];

export const THIRD_PARTY_BACKGROUND_ACTIVITY_URLS = [
    /\.pingify\.prod\.geocomply\.com/,
    /apps\.rokt-api\.com/,
    /^https?:\/\/[^/]+\.on\.aws(?:[/?]|$)/,
    /^https?:\/\/(?:[^/]+\.)?hypelab\.com(?:[/?]|$)/,
    /^https?:\/\/(?:[^/]+\.)?apiarydata\.net(?:[/?]|$)/,
    /^https?:\/\/(?:[^/]+\.)?adrsbl\.io(?:[/?]|$)/,
    /^https?:\/\/[^/]+\.a\.run\.app(?:[/?]|$)/,
    /^https?:\/\/(?:www\.)?facebook\.com\/tr(?:[/?]|$)/,
];

export function initDatadog(): void {
    if (started) return;
    started = true;

    installFetchFailureContext();

    datadogRum.init(
        {
            applicationId: '1994ded0-b749-492e-83a3-9e064b5ebbed',
            clientToken: 'pub222856fe11852abc3642b1395fac0627',
            // `site` refers to the Datadog site parameter of your organization
            // see https://docs.datadoghq.com/getting_started/site/
            site: 'datadoghq.com',
            service: 'trading-portal',
            env: config.frontendEnv === 'production' ? 'prod' : config.frontendEnv,
            // Specify a version number to identify the deployed version of your application in Datadog
            version: config.commitRef,
            sessionSampleRate: 100,
            sessionReplaySampleRate: 1,
            defaultPrivacyLevel: 'mask-user-input',
            // Names SPA views after App Router route patterns via <DatadogAppRouter />
            // (app/layout.tsx) + onRouterTransitionStart (instrumentation-client.ts).
            // Safe to import statically: instrumentation-client eagerly loads the same
            // module, and the ~1 MB SDK itself stays behind the dynamic import.
            plugins: [nextjsPlugin()],
            beforeSend: (event, domainContext) => isFirstPartyError(event, domainContext),
            allowedTracingUrls: [{ match: /^https?:\/\/([^/]+\.)?kalshi\.com\//, propagatorTypes: ['tracecontext'] }],
            propagateTraceBaggage: false,
            traceSampleRate: 100,
            // Applied globally — filters these URLs from activity tracking for both hard and SPA views.
            excludedActivityUrls: [
                // Third party
                /awswaf/,
                /s\.axon\.ai/,
                /cdn\.lr-ingest\.io/,
                /static\.ads-twitter\.com/,
                /analytics\.twitter\.com/,
                /applovin\.com/,
                /t\.co/,
                /tiktok/,
                /js\.stripe\.com/,
                /verifi\.pdscrb\.com/,
                /ipv4\.pdscrb\.com/,
                /featureassets\.org/,
                /google/,
                /riskified\.com/,
                /api\.sprig\.com/,
                /arttrk\.com/,
                /d34r8q7sht0t9k\.cloudfront\.net/,
                /reddit\.com/,
                /redditstatic\.com/,
                /tag\.tvads\.ai/,
                /doubleclick/,
                /google\.co\.kr/,
                /amplitude/,
                /mgln\.ai/,
                /verify\.pdscrb\.com/,
                /prodregistryv2\.org/,
                /trkn\.us/,
                /snap\.licdn\.com/,
                /px\.ads\.linkedin\.com/,
                /connect\.universalads\.com/,
                /beehiiv-adnetwork-production\.s3\.amazonaws\.com/,
                /pixels\.spotify\.com/,
                /apps\.rokt\.com/,
                /id5-sync\.com/,
                /secure\.adnxs\.com/,
                /pixel\.springserve\.com/,
                /adsmeasurement/,
                /connect\.facebook\.net/,
                ...THIRD_PARTY_BACKGROUND_ACTIVITY_URLS,
                // Internal polling endpoints that create noise in RUM metrics without user action
                ...INTERNAL_BACKGROUND_ACTIVITY_URLS,
                // Display enhancements that continue polling after the page is usable
                ...NON_CRITICAL_DISPLAY_ACTIVITY_URLS,
            ],
        },
        // nextjsPlugin switches the SDK to manual view tracking, where RUM does not
        // start collecting anything until the first `startView()`. <DatadogAppRouter />
        // drives startView on route changes, but it renders before this deferred init,
        // so its initial call lands while the plugin has no SDK handle and is dropped —
        // without this kick-start, sessions that never client-navigate would record
        // nothing. Named with the normalized route pattern captured by
        // <DatadogRouteTracker /> (getInitialRumViewName), matching the plugin's naming for
        // every subsequent SPA view — otherwise direct/refresh sessions on dynamic routes
        // would be recorded under the concrete URL and fragment RUM metrics. Falls back to
        // the SDK's URL-derived default if the tracker hasn't populated it yet.
        //
        // Runs as the `onReady` hook (before the facade's buffered queue drains) rather
        // than as a buffered `datadogRum.startView()` call: the initial view must exist
        // before earlier-buffered `setViewName`/`addTiming` calls replay, or they land in
        // "no view" and are lost. Guarded separately from `started` because disposeDatadog()
        // resets that for re-init, and a redundant startView on the running SDK would split
        // the current view.
        //
        // Goes through the plugin's own `startNextjsView` rather than `rum.startView` so the
        // URL captured by `onRouterTransitionStart` (instrumentation-client.ts) is honored: on
        // a client navigation that began before init, `window.location` can still point at the
        // previous page, so a bare startView would record the destination's *name* against the
        // previous page's URL. `startNextjsView` also clears that captured URL, which a direct
        // call would leave set for a later view to pick up. It is a no-op until the plugin has
        // the SDK handle, but `nextjsPlugin`'s `onInit` runs synchronously inside
        // `datadogRum.init()` above (rum-core `preStartRum`), so the handle exists by now.
        (rum) => {
            if (!initialViewStarted) {
                initialViewStarted = true;
                const initialViewName = getInitialRumViewName();
                if (initialViewName) {
                    startNextjsView(initialViewName);
                } else {
                    // Tracker hasn't populated a name yet (shouldn't happen — its layout effect
                    // runs long before this deferred init). Fall back to the SDK's URL-derived
                    // default; `startNextjsView` requires a name.
                    rum.startView();
                }
            }
        },
    );

    // Identify returning users who were already logged in before Datadog loaded
    const userId = useAuthStore.getState().userId;
    if (userId) {
        datadogRum.setUser({ id: userId });
    }

    memoryMonitor.start();
    datadogContextManager.start();
    setupViewLoadingTime();
    // Observation already started at `instrumentation-client`; this only attaches the
    // RUM publication side and drains whatever accumulated before the SDK existed.
    startMainThreadPublishing();

    loaded = true;
    markDatadogSessionLoaded();
    for (const fn of pendingReplayCalls.splice(0)) fn();
}

/**
 * Stops datadogContextManager's poll and drops its store reference, and
 * resets the `started`/`loaded` guards so a subsequent `initDatadog()` (e.g.
 * after `AppEffectsBootstrap` unmounts and a later mount restarts the app
 * effects) restarts the context poller against the new store instead of
 * silently no-op'ing forever. Safe to re-run: `datadogRum.init` is itself
 * idempotent (module-level `loadPromise` guard), `memoryMonitor.start()` just
 * resets plain fields, and `markLoaded`/`setUser` re-application are no-ops
 * beyond redundant work.
 */
export function disposeDatadog(): void {
    datadogContextManager.stop();
    stopMainThreadBlocking();
    started = false;
    loaded = false;
}

/**
 * Force-start session replay recording on the current session, ignoring the
 * global `sessionReplaySampleRate`. Deferred until `initDatadog()` completes so
 * the `{ force: true }` option survives — when called pre-`datadogRum.init()`,
 * the SDK's preStartStrategy drops options and falls back to sample-rate
 * behavior. Does not itself trigger `initDatadog()` so non-prod (where the SDK
 * never loads) does not get accidentally initialized just because a caller mounted.
 */
export function forceSessionReplayRecording(): void {
    const go = (): void => {
        datadogRum.startSessionReplayRecording({ force: true });
    };
    if (loaded) {
        go();
    } else {
        pendingReplayCalls.push(go);
    }
}
